/**
 * <lainly-rail>: a sideways-swipe row of cards, with dots that follow the card in view.
 * Markup: the scrolling list carries [data-rail-track]; each dot carries [data-rail-dot].
 * Optional: [data-rail-prev] / [data-rail-next] buttons move one card at a time, and
 * data-rail-start="2" on the element opens the row centered on that card (1-based), so
 * cards peek in on both sides. Layout and snapping live in each section's CSS.
 */
if (!customElements.get('lainly-rail')) {
  customElements.define(
    'lainly-rail',
    class LainlyRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rail-track]');
        if (!this.track) return;

        this.dots = [...this.querySelectorAll('[data-rail-dot]')];
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        this.controller = new AbortController();
        const { signal } = this.controller;

        this.track.addEventListener('scroll', () => this.update(), { passive: true, signal });
        this.track.addEventListener('scrollend', () => this.update(), { signal });
        this.prev?.addEventListener('click', () => this.step(-1), { signal });
        this.next?.addEventListener('click', () => this.step(1), { signal });

        const start = Number(this.dataset.railStart) - 1;
        if (start > 0) this.center(start, 'instant');
        this.update();
      }

      disconnectedCallback() {
        this.controller?.abort();
      }

      /** Index of the card closest to the middle of the row. */
      get activeIndex() {
        const cards = [...this.track.children];
        const middle = this.track.scrollLeft + this.track.clientWidth / 2;
        const distance = (card) => Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle);
        let active = 0;
        cards.forEach((card, index) => {
          if (distance(card) < distance(cards[active])) active = index;
        });
        return active;
      }

      /** Scrolls so the card at `index` sits in the middle of the row. */
      center(index, behavior = 'smooth') {
        const card = this.track.children[index];
        if (!card) return;
        const left = card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2;
        this.track.scrollTo({ left, behavior });
      }

      step(direction) {
        const last = this.track.children.length - 1;
        const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
        this.center(Math.min(last, Math.max(0, this.activeIndex + direction)), behavior);
      }

      update() {
        const active = this.activeIndex;
        this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
        if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll - 1;
      }
    }
  );
}
